import { SectionHeader } from '../common/SharedComponents';
import { PaymentTable } from './tables/PaymentTable';
import { PaymentStatusFilter, StatusCounts } from './components/PaymentStatusFilter';
import { useMemo, useState } from 'react';
import { DateFilter } from '../common/SharedComponents';
import { PayeeEditModal } from './modals/PaymentEditModal';
import { PaymentRefundModal } from './modals/PaymentRefundModal';
import { Payment, PaymentApi, PaymentGridRow } from '@apply/api/src/api/PaymentApi';
import { PaymentStatus } from '@apply/constants';
import { CustomStore } from '@apply/ui';
import { SearchResponse } from '@apply/api/src/api/bfiApi';
import { applicationInsights } from '../ApplicationInsights';
import { snackbarError, snackbarSuccess } from '../common/functions/snackbarFunctions';
import Query from 'devextreme/data/query';

export const PaymentSummary: React.FC = () => {
    const [editModalPayment, set_editModalPayment] = useState<Payment | undefined>(undefined);
    const [refundModalPayment, set_refundModalPayment] = useState<Payment | undefined>(undefined);
    const [refreshGrid, set_refreshGrid] = useState<boolean>(false);

    const today = new Date();
    const [startDate, set_startDate] = useState<Date>(new Date(today.getFullYear(), today.getMonth(), 1));
    const [endDate, set_endDate] = useState<Date>(new Date());
    const [filterPaymentStatus, set_filterPaymentStatus] = useState<PaymentStatus | undefined>(undefined);

    const [statusCounts, set_statusCounts] = useState<StatusCounts>({
        all: 0,
        inProgress: 0,
        failure: 0,
        refund: 0,
        success: 0,
    });

    const paymentApi = new PaymentApi(applicationInsights);

    const save_bfiNumber = async (paymentId: number, bfiNumber: string) => {
        if (!editModalPayment) return;

        const response = await paymentApi.setBfiNumber({ paymentId, bfiNumber });

        if (response.statusCode === 200) {
            set_editModalPayment({ ...editModalPayment, bfiNumber: bfiNumber });
            set_refreshGrid(!refreshGrid);
            snackbarSuccess('BFI Number Saved');
        } else {
            snackbarError('There was an error saving the BFI Number.');
            // TODO log to application insights
        }
    };

    const customStore = useMemo(
        () =>
            new CustomStore({
                key: 'paymentId',
                load: async (options) => {
                    const response: SearchResponse<PaymentGridRow[]> = await paymentApi.getPaymentGrid({
                        startDate,
                        endDate,
                        paymentStatus: filterPaymentStatus,
                    });

                    if (options.filter) {
                        response.result.data = Query(response.result.data).filter(options.filter).toArray();
                    }

                    if (filterPaymentStatus === undefined) calculateStatusTotals(response.result.data);

                    return response.result;
                },
            }),
        [startDate, endDate, filterPaymentStatus, refreshGrid]
    );

    function calculateStatusTotals(data: PaymentGridRow[]) {
        const counts: StatusCounts = {
            all: 0,
            inProgress: 0,
            failure: 0,
            refund: 0,
            success: 0,
        };

        data.forEach((d) => {
            switch (d.paymentStatus) {
                case PaymentStatus.InProgress:
                    counts.inProgress++;
                    break;
                case PaymentStatus.Failure:
                    counts.failure++;
                    break;
                case PaymentStatus.Success:
                    counts.success++;
                    break;
                case PaymentStatus.Refund:
                case PaymentStatus.RefundInProgress:
                case PaymentStatus.RefundFailure:
                    counts.refund++;
                    break;
            }

            counts.all = counts.inProgress + counts.failure + counts.success + counts.refund;
        });

        set_statusCounts(counts);
    }

    const onRefundModalRefunded = (payment: Payment) => {
        if (editModalPayment) set_editModalPayment(payment);
        set_refreshGrid(!refreshGrid);
    };

    return (
        <>
            <SectionHeader title='Manage Payments' />
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div
                    className='scrollable-content-without-search'
                    style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                    <PaymentStatusFilter
                        filterPaymentStatus={filterPaymentStatus}
                        set_paymentStatus={set_filterPaymentStatus}
                        {...statusCounts}
                    />
                    <DateFilter
                        startDate={startDate}
                        set_startDate={set_startDate}
                        endDate={endDate}
                        set_endDate={set_endDate}
                    />
                    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <PaymentTable
                            customStore={customStore}
                            set_refundModalPayment={set_refundModalPayment}
                            set_editModalPayment={set_editModalPayment}
                        />
                    </div>
                </div>
            </div>
            <PayeeEditModal
                payment={editModalPayment}
                set_editModalPayment={set_editModalPayment}
                set_refundModalPayment={set_refundModalPayment}
                save_bfiNumber={save_bfiNumber}
            />
            <PaymentRefundModal
                payment={refundModalPayment}
                set_refundModalPayment={set_refundModalPayment}
                onRefundModalRefunded={onRefundModalRefunded}
            />
        </>
    );
};
